import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import api from '../utils/api';
import { getErrorMessage } from '../utils/errors';

/**
 * Tier-3 step-up (security audit 2026-09-01 §2.3 / §2.MED.22).
 *
 * The API refuses SSN reveal / Tier-3 download / license parse with
 * 403 {code:"mfa_step_up_required"} when the session's authenticator check is
 * older than the step-up window, and with {code:"mfa_enrollment_required"}
 * when the dealership requires MFA for the caller's role and they have not
 * enrolled. utils/api.js turns either into a `dealerflow:mfa-required` window
 * event; this modal (mounted once in App.js) collects the code, swaps the
 * session token for the fresh one, and asks the user to retry the action.
 */
export const MFA_REQUIRED_EVENT = 'dealerflow:mfa-required';

export default function MfaStepUpModal() {
  const [detail, setDetail] = useState(null);
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const onRequired = (e) => {
      setDetail(e.detail || { code: 'mfa_step_up_required' });
      setCode(''); setPassword(''); setError(null);
    };
    window.addEventListener(MFA_REQUIRED_EVENT, onRequired);
    return () => window.removeEventListener(MFA_REQUIRED_EVENT, onRequired);
  }, []);

  const close = useCallback(() => setDetail(null), []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      const body = { code: code.trim() };
      if (detail?.password_required) body.password = password;
      const { data } = await api.post('/auth/mfa/step-up', body);
      if (data?.token) localStorage.setItem('token', data.token);
      toast.success('Verified — please retry that action.');
      setDetail(null);
    } catch (err) {
      setError(getErrorMessage(err, 'Verification failed'));
    } finally {
      setBusy(false);
    }
  };

  if (!detail) return null;
  const enrollmentRequired = detail.code === 'mfa_enrollment_required';

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="mfa-step-up-modal">
      <div className="w-full max-w-sm rounded-lg border border-border bg-card p-5 shadow-xl">
        <h2 className="text-lg font-semibold text-foreground">
          {enrollmentRequired ? 'Two-factor setup required' : 'Verify it’s you'}
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          {enrollmentRequired
            ? 'Your dealership requires an authenticator app for this action. Set one up under Settings → Security, then try again.'
            : (detail.msg || 'Enter the 6-digit code from your authenticator app (or a recovery code) to continue.')}
        </p>
        {enrollmentRequired ? (
          <div className="mt-4 flex justify-end gap-2">
            <button type="button" onClick={close} className="rounded-md border border-input px-3 py-2 text-sm">Close</button>
            <a href="/settings" className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700">Open Settings</a>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-3">
            {detail.password_required && (
              <label className="block text-sm">
                <span className="text-muted-foreground">Password (this session was started by text message)</span>
                <input type="password" autoComplete="current-password" value={password}
                  onChange={(e) => setPassword(e.target.value)} required
                  className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              </label>
            )}
            <label className="block text-sm">
              <span className="text-muted-foreground">Authenticator code</span>
              <input inputMode="numeric" autoComplete="one-time-code" value={code} autoFocus
                onChange={(e) => setCode(e.target.value)} required data-testid="mfa-step-up-code"
                className="mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm tracking-widest" />
            </label>
            {error && <p className="text-sm text-red-600 dark:text-red-400" role="alert">{error}</p>}
            <div className="flex justify-end gap-2">
              <button type="button" onClick={close} className="rounded-md border border-input px-3 py-2 text-sm">Cancel</button>
              <button type="submit" disabled={busy}
                className="rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {busy ? 'Verifying…' : 'Verify'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
